import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Box } from '@mui/system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// react icons
import { FaCity, FaIdBadge, FaRegUser, FaUserTie } from 'react-icons/fa';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { GiMoneyStack, GiPayMoney, GiTakeMyMoney } from 'react-icons/gi';
import { HiLocationMarker, HiReceiptTax } from 'react-icons/hi';
import {
  MdEmail,
  MdOutlineDateRange,
  MdOutlineMoreHoriz,
  MdOutlineSendToMobile,
  MdPeople,
  MdPhone
} from 'react-icons/md';
import { TbReceiptTax } from 'react-icons/tb';
// material icons
import AddCardIcon from '@mui/icons-material/AddCard';
import AdUnitsIcon from '@mui/icons-material/AdUnits';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import EventNoteIcon from '@mui/icons-material/EventNote';
import GroupIcon from '@mui/icons-material/Group';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { BsGenderAmbiguous } from 'react-icons/bs';
import { AiOutlineApartment } from 'react-icons/ai';
import { RiUserSettingsLine } from 'react-icons/ri';
import { fDate } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';
// componenets
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getUserList, getUserViewList } from '../../store/Slice/User/UserSlice';
import usePayoutSelector from '../../store/Slice/Payout/PayoutSelector';
import {
  getBankDropdownData,
  getCityData,
  getLoanTypeData,
  saveApiData
} from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import { getPayoutCommentLogs, getPayoutViewData } from '../../store/Slice/Payout/PayoutSlice';
import { getRevenuePartnerDetails } from '../../store/Slice/Revenue/RevenueSlice';
import ViewCardComponent from '../../components/ViewCards';
import { normalizeValue } from '../../utils/CommonFunction';
import ViewLargeCard from '../../components/ViewLargeCard';
import { StyledIconButton, StyledTableRow } from '../../utils/StyleTable';
import { PayoutApi, UserApi } from '../../utils/Apis';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';
import { setApiError } from '../../store/Slice/Error/ErrorSlice';
// import useUserSelector from 'src/store/Slice/User/UserSelector';
import useUserSelector from '../../store/Slice/User/UserSelector';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    padding: '6px',
    height: '42.5px',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: '#0d4689'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    padding: '4px 6px',
    height: '42.5px'
  }
}));

export const addPayoutValidationSchema = yup.object().shape({
  fileNumber: yup
    .string()
    .required('Please add file no')
    .max(50, 'File No must be less than 50 characters'),

  fileSubmissionDate: yup.string().required('Please add submission date'),

  bankId: yup
    .string()
    .nullable()
    .required('Bank Name is required')
    .notOneOf(['', ' ', null], 'Please select a valid Bank'),
  cityId: yup
    .string()
    .nullable()
    .required('City Name is required')
    .notOneOf(['', ' ', null], 'Please select a valid City'),

  borrowerName: yup
    .string()
    .required('Please add borrower name')
    .max(50, 'Borrower Name must be less than 50 characters'),

  loanTypeId: yup
    .string()
    .required('Please select loan type')
    .notOneOf(['', ' ', null], 'Please select a valid Loan Type'),

  disburseMonth: yup.string().required('Please add disbursed month'),

  disburseAmount: yup
    .number()
    .typeError('Please add disbursed amount')
    .required('Please add disbursed amount')
    .positive('Disbursed Amount must be greater than 0')
    .max(10000000000, 'Please add disbursed amount less than 10,00,00,00,000'),

  sanctionedMonth: yup.string().required('Please add sanctioned month'),

  sanctionedAmount: yup
    .number()
    .typeError('Please add sanctioned amount')
    .required('Please add sanctioned amount')
    .positive('Sanctioned Amount must be greater than 0')
    .max(10000000000, 'Please add sanctioned amount less than 10,00,00,00,000'),

  encashmentMonth: yup.string().required('Please add encashment month'),

  encashmentAmount: yup
    .number()
    .typeError('Please add encashment amount')
    .required('Please add encashment amount')
    .positive('Encashment Amount must be greater than 0')
    .max(10000000000, 'Please add encashment amount less than 10,00,00,00,000')
});

function ViewUser() {
  const [editFile, setEditFile] = useState(false);
  const { UserViewListData } = useUserSelector();
  const navigate = useNavigate();
  // console.log('sss user view data', UserViewListData);

  const {
    handleSubmit: handleReduxSubmit,
    control,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onBlur', // Add this to validate on blur
    defaultValues: {
      fileNumber: '',
      fileSubmissionDate: '',
      bankId: '',
      cityId: '',
      borrowerName: '',
      loanTypeId: '',
      disburseMonth: '',
      disburseAmount: '',
      sanctionedMonth: '',
      sanctionedAmount: '',
      encashmentMonth: '',
      encashmentAmount: ''
    }
  });

  const params = useParams();

  const rowId = params?.rowId;
  // console.log('view user rowId', rowId);
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { LoanTypeDrop, BankDrop, CityDrop } = useGlobalSelector();

  const handleEditOpen = useCallback(
    async ({ userId, action }) => {
      // console.log('print the userid', userId);
      const endpoint = `${UserApi?.viewUser}/${userId}?loggedInUserId=${LoginData?.userId}`;
      const result = await dispatch(getUserViewList(endpoint));

      // console.log('sss view user result', result);

      if (result && action === 'view') {
        navigate(`/users/view/${userId}`);
      } else if (result && action === 'edit') {
        navigate(`/users/edit/${userId}`);
      }
    },
    [dispatch, navigate, LoginData?.userId]
  );

  // loan type & Bank Dropdown

  // city api
  const handleGetState = (optionVal) => {
    const data = {
      stateId: optionVal
    };
    dispatch(getCityData(data)); // city data
  };

  useEffect(() => {
    handleGetState(null); // city api
    dispatch(getRevenuePartnerDetails()); // partner details
  }, []);

  const payoutFields = useMemo(
    () => [
      {
        label: 'User ID',
        value: UserViewListData?.userId,
        icon: <FaIdBadge className="viewLabelIcon" size={22} />
      },
      {
        label: 'First Name',
        value: UserViewListData?.firstName,
        icon: <FaRegUser className="viewLabelIcon" size={22} />
      },
      {
        label: 'Last Name',
        value: UserViewListData?.lastName,
        icon: <FaRegUser className="viewLabelIcon" size={22} />
      },
      {
        label: 'Email',
        value: UserViewListData?.email,
        icon: <MdEmail className="viewLabelIcon" size={22} />
      },
      {
        label: 'Gender',
        value: UserViewListData?.gender,
        icon: <BsGenderAmbiguous className="viewLabelIcon" size={22} />
      },
      {
        label: 'Mobile',
        value: UserViewListData?.mobile,
        icon: <MdPhone className="viewLabelIcon" size={22} />
      },
      {
        label: 'City',
        value: UserViewListData?.cityName,
        icon: <FaCity className="viewLabelIcon" size={22} />
      },
      {
        label: 'State',
        value: UserViewListData?.stateName,
        icon: <HiLocationMarker className="viewLabelIcon" size={22} />
      },
      {
        label: 'Status',
        value: UserViewListData?.status,
        icon: <MdPeople className="viewLabelIcon" size={22} />
      },
      {
        label: 'User Type',
        value: UserViewListData?.userType,
        icon: <RiUserSettingsLine className="viewLabelIcon" size={22} />
      },
      {
        label: 'Reporting Manager Name',
        value: UserViewListData?.reportingManagerName,
        icon: <FaUserTie className="viewLabelIcon" size={22} />
      },
      {
        label: 'Department Name',
        value: UserViewListData?.departmentName,
        icon: <AiOutlineApartment className="viewLabelIcon" size={22} />
      },
      {
        label: 'Access Level Name',
        value: UserViewListData?.accessLevelName,
        icon: <MdEmail className="viewLabelIcon" size={22} />
      },
      {
        label: 'Relationship Manager Name',
        value: UserViewListData?.relationshipManagerName,
        icon: <FaUserTie className="viewLabelIcon" size={22} />
      }
    ],
    [UserViewListData]
  );

  return (
    <Page title="Partner Management System || Payout">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="View File" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <ViewLargeCard
            title="User Details"
            fields={payoutFields || []}
            handleEdit={handleEditOpen}
            rowId={rowId}
          />
        </Box>
      </Container>
    </Page>
  );
}
export default ViewUser;
